type ErrorWithMessage = {
  message: string;
};
type ResponseWithMessage = {
  response: {
    data: {
      error: string;
    };
  };
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}
function isResponseWithDataError(error: unknown): error is ResponseWithMessage {
  const hasResponse = typeof error === 'object' && error !== null && 'response' in error;
  if (!hasResponse) return false;
  const { response } = error as ResponseWithMessage;
  const hasResponseData = typeof response === 'object' && response !== null && 'data' in response;
  if (!hasResponseData) return false;
  const { data } = response as ResponseWithMessage['response'];
  if (!data) return false;
  return typeof data.error === 'string';
}

const isResponseString = (error: unknown): error is string => typeof error === 'string';

// Return an { error: string } object from any error type.
// Resolves strings first, then morecomplex axios errors if it is one. Strings are assumed to be error messages, useful for sending custom error messages.
function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isResponseString(maybeError)) return { message: maybeError };
  if (isResponseWithDataError(maybeError)) return { message: maybeError.response.data.error };
  if (isErrorWithMessage(maybeError)) return maybeError;

  // Assuming neither of the cases above pass, we'll try to stringify the maybeError
  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

// This is really only attempting to get api error responses, not all errors like native Errors or Axios errors.
// To be deprecated in favor of getErrorMessage_v2
const getErrorMessage = (error: any) => {
  // Allow to return the message from native Error events.
  let errorMssg = isErrorWithMessage(error) ? error.message : 'Something went wrong';
  if (error.response && error.response.data.error) {
    try {
      errorMssg = JSON.parse(error.response.data.error).error;
    } catch (customError: any) {
      errorMssg = error.response.data.error;
    }
  }
  return errorMssg;
};

function getErrorMessage_v2(error: unknown, fallback?: string) {
  if (!error) return fallback ?? 'An error occurred';
  return toErrorWithMessage(error).message;
}

export { getErrorMessage, getErrorMessage_v2 };
